import { useEffect, useState, useMemo } from 'react';
import { SelectField } from '@sainsburys-tech/luna-form';
import { useComponentContext } from '../../ComponentContext';
import { IDynamicDropdown } from '../../../../../types/ComponentTypes/Components';
import { getDropdownData } from '../../../../../api/reportingApi';
import { useApiClient } from '../../../../../context/AppContext';
import { IDropdownData, IDropdownDataOption } from '../../../../../types';

function DynamicDropdownComponent({ label, id, error, answer, placeholder, service, parameters }: IDynamicDropdown) {
  const { handleUpdate, handleGetAnswer, handleGetAnswers } = useComponentContext();
  const [parameterValues, setParameterValues] = useState<any>(null);
  const [disabled, setDisabled] = useState(false);
  const [loading, setLoading] = useState<boolean | null>(null);
  const [options, setOptions] = useState<IDropdownDataOption[]>([]);
  const apiClient = useApiClient();
  const [answers, setAnswers] = useState(null);

  const handleChange = (e: any) => {
    handleUpdate({ id, value: e.target.value });
  };

  // Memoize parameterIds to prevent unnecessary re-renders
  const parameterIds = useMemo(() => {
    return parameters
      .filter((p) => p.value === '' || p.value === null)
      .map((p) => p.componentId!);
  }, [parameters]);

  // Update answers only when parameterIds change
  useEffect(() => {
    const newAnswers = handleGetAnswers({ ids: parameterIds });
    setAnswers((prevAnswers) => (JSON.stringify(prevAnswers) !== JSON.stringify(newAnswers) ? newAnswers : prevAnswers));
  }, [parameterIds, handleGetAnswers]);

  // Update parameterValues based on parameters and answers
  useEffect(() => {
    const tempParamValues: any[] = [];

    parameters.forEach((p) => {
      const paramValue = p.value !== '' ? p.value : handleGetAnswer({ id: p.componentId! });
      if ((paramValue !== undefined && paramValue !== null && paramValue !== '') || p.allowEmpty) {
        tempParamValues.push({ key: p.key, value: paramValue });
      }
    });

    let emptyCount = 0;

    parameters
      .filter((p) => !p.allowEmpty)
      .forEach((p) => {
        if (!tempParamValues.find((tp) => tp.key === p.key)) {
          emptyCount += 1;
        }
      });

    const arraysAreEqual = JSON.stringify(tempParamValues?.sort()) === JSON.stringify(parameterValues?.sort());

    if (!arraysAreEqual && emptyCount === 0) {
      setParameterValues(tempParamValues);
    }

    if (emptyCount !== 0) {
      setOptions([]);
      setDisabled(true);
    }
  }, [answers, parameters, handleGetAnswer, parameterValues]);

  // Fetch dropdown data when parameterValues change
  useEffect(() => {
    const fetchDropdownData = async () => {
      if (parameterValues === null) {
        setDisabled(true);
        return;
      }

      setLoading(true);
      setDisabled(true);
      setOptions([]);

      try {
        const response: IDropdownData = await getDropdownData(
          apiClient,
          service,
          parameterValues.reduce(
            (obj: any, item: { key: any; value: any }) => Object.assign(obj, { [item.key]: item.value }),
            {}
          )
        );

        setLoading(false);

        if (response.options.length !== 0) {
          setOptions(response.options);
          setDisabled(false);
        }
      } catch (error) {
        setLoading(false);
        setDisabled(true);
      }
    };

    fetchDropdownData();
  }, [parameterValues, apiClient, service]);

  return (
    <div id="a-logging-dynamic-dropdown" tabIndex={-1}>
      <SelectField
        name={id}
        label={label}
        className="a-logging-dynamic-dropdown"
        error={error}
        options={options}
        onChange={handleChange}
        disabled={disabled}
        placeholder={loading ? 'Loading...' : placeholder || 'Please Select'}
        value={answer || ''}
      />
    </div>

  );
}

export default DynamicDropdownComponent;